<!-- Tab bar -->
<div class="flex border-b border-slate-200 px-6" role="tablist">
  <button
    *ngFor="let tab of tabs"
    (click)="selectedTab = tab.id"
    role="tab"
    [attr.aria-selected]="selectedTab === tab.id"
    class="mr-6 border-b-2 py-3 text-sm font-medium transition-colors focus:outline-none"
    [class.border-violet-600]="selectedTab === tab.id"
    [class.text-violet-700]="selectedTab === tab.id"
    [class.border-transparent]="selectedTab !== tab.id"
    [class.text-slate-500]="selectedTab !== tab.id"
    [class.hover:text-slate-700]="selectedTab !== tab.id"
  >
    {{ tab.label }}
  </button>
</div>

<!-- ── Overview ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'overview'" class="p-6" role="tabpanel">

  <div class="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
    <p class="text-xs text-amber-700">
      Cognito monitoring is conservative in this version: it detects request <span class="font-medium">throttling</span> at the user-pool level (CloudWatch <span class="font-mono">UserPool</span> dimension). Cognito does not publish a native sign-in <span class="font-medium">failure</span> metric, so failed-auth alerting is not yet available.
    </p>
  </div>

  <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">User Pools</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ totalCount }}</p>
    </div>
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-emerald-500">Monitored</p>
      <p class="mt-1 text-2xl font-bold text-emerald-700">{{ monitoredCount }}</p>
    </div>
  </div>

</div>

<!-- ── User Pools ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'userPools'" role="tabpanel">

  <app-cloud-filter-bar
    entityName="user pools"
    placeholder="Search name or id…"
    [facets]="facets"
    [sortOptions]="sortOptions"
    [filters]="filters"
    [resultCount]="filteredPools.length"
    [totalCount]="userPools.length"
    [quotaUsage]="quotaUsage"
    (filtersChange)="onFiltersChange($event)"
  ></app-cloud-filter-bar>

  <div class="flex min-h-0" [class.divide-x]="selectedId" [class.divide-slate-200]="selectedId">

   <div class="min-w-0 flex-1 p-6">

    <div *ngIf="loading" class="flex items-center gap-2 py-6 text-sm text-slate-400">
      <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
      Loading Cognito user pools…
    </div>

    <div *ngIf="!loading" class="overflow-x-auto">
      <table class="w-full min-w-[600px] text-left text-sm">
        <thead>
          <tr class="border-b border-slate-200">
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">User Pool</th>
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Monitoring</th>
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Routing</th>
            <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">History</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let pool of filteredPools"
              class="border-b border-slate-100 last:border-0"
              [class.bg-violet-50]="pool.id === selectedId">
            <td class="py-3 pr-4">
              <span class="text-xs font-medium text-slate-900">{{ pool.name }}</span>
              <p class="mt-0.5 font-mono text-[10px] text-slate-400">{{ pool.id }}</p>
            </td>
            <!-- Monitoring -->
            <td class="py-3 pr-4 whitespace-nowrap">
              <ng-container *ngIf="isMonitored(pool.id); else cognitoNotMonitored">
                <button
                  type="button"
                  (click)="toggleMonitoring(pool.id)"
                  class="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  aria-label="Disable monitoring"
                >
                  <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                  Monitored
                </button>
              </ng-container>
              <ng-template #cognitoNotMonitored>
                <ng-container *ngIf="canMonitorMore; else cognitoQuotaFull">
                  <button
                    type="button"
                    (click)="toggleMonitoring(pool.id)"
                    class="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  >
                    Monitor
                  </button>
                </ng-container>
                <ng-template #cognitoQuotaFull>
                  <span class="group relative inline-block">
                    <button
                      type="button"
                      (click)="toggleMonitoring(pool.id)"
                      aria-disabled="true"
                      class="cursor-not-allowed rounded border border-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-300 focus:outline-none"
                    >
                      Monitor
                    </button>
                    <span class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[10px] leading-tight text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                      Quota reached
                    </span>
                  </span>
                </ng-template>
              </ng-template>
            </td>
            <!-- Routing -->
            <td class="py-3 pr-4 whitespace-nowrap">
              <div class="flex items-center gap-1.5">
                <span *ngIf="itemOverrideSummary(pool.id)"
                      class="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600">
                  {{ itemOverrideSummary(pool.id) }}
                </span>
                <button
                  type="button"
                  (click)="openItemSettings(pool.id)"
                  class="flex h-5 w-5 items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.text-violet-500]="!!itemOverrideSummary(pool.id)"
                  [attr.aria-label]="'Configure routing for ' + pool.name"
                >
                  <lucide-icon name="settings" [size]="12" [strokeWidth]="2"></lucide-icon>
                </button>
              </div>
            </td>
            <!-- History -->
            <td class="py-3 pr-2 whitespace-nowrap">
              <button
                type="button"
                (click)="pool.id === selectedId ? closeResource() : selectResource(pool.id)"
                class="rounded border px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                [class.border-violet-300]="pool.id === selectedId"
                [class.bg-violet-50]="pool.id === selectedId"
                [class.text-violet-700]="pool.id === selectedId"
                [class.border-slate-200]="pool.id !== selectedId"
                [class.text-slate-500]="pool.id !== selectedId"
                [class.hover:border-slate-300]="pool.id !== selectedId"
                [class.hover:text-slate-700]="pool.id !== selectedId"
                [attr.aria-label]="(pool.id === selectedId ? 'Hide' : 'View') + ' metric history for ' + pool.id"
              >
                {{ pool.id === selectedId ? 'Close' : 'History' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty state: no matches -->
      <div *ngIf="filteredPools.length === 0 && userPools.length > 0" class="py-10 text-center">
        <lucide-icon name="search" [size]="28" class="mx-auto mb-2 text-slate-300" [strokeWidth]="1.5"></lucide-icon>
        <p class="text-sm font-medium text-slate-500">No user pools match your filters</p>
        <p class="mt-0.5 text-xs text-slate-400">Try adjusting the search or clearing active filters.</p>
      </div>

      <!-- Empty state: discovery error (AccessDenied / endpoint unreachable) -->
      <div *ngIf="userPools.length === 0 && loadError" class="my-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <p class="text-xs font-semibold text-amber-800">Couldn't list Cognito user pools</p>
        <p class="mt-1 break-words text-xs text-amber-700">{{ loadError }}</p>
        <p class="mt-2 text-xs text-amber-700">
          Most often this means the cross-account role is missing the <span class="font-mono">cognito-idp:ListUserPools</span> permission. Re-copy the IAM policy from Settings → Integrations → AWS, or check that your user pools are in the connection's region.
        </p>
      </div>

      <!-- Empty state: no data (no error) -->
      <p *ngIf="userPools.length === 0 && !loadError" class="py-6 text-sm text-slate-400">
        No Cognito user pools found in this account and region.
      </p>
    </div>

   </div>

   <!-- Metric history drill-down panel -->
   <div *ngIf="selectedId" class="w-96 shrink-0">
     <app-cloud-metrics-panel
       [serviceId]="'cognito'"
       [resourceId]="selectedId"
       [metrics]="historyMetrics"
       (closed)="closeResource()"
     ></app-cloud-metrics-panel>
   </div>

  </div>

</div>

<!-- ── Activity ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'activity'" role="tabpanel">
  <app-cloud-activity-feed
    [items]="activity"
    [loading]="activityLoading"
    serviceLabel="Cognito"
    emptyHint="Throttling findings (opened / resolved) appear here."
    [note]="'Cognito signal is limited to throttling — sign-in failures aren\'t exposed as a CloudWatch metric.'"
    (itemSelect)="onActivitySelect($event)"
  ></app-cloud-activity-feed>
</div>

<!-- ── Item settings drawer ──────────────────────────────────────── -->
<app-item-settings-drawer
  [service]="serviceCard"
  [itemId]="drawerItemId"
  [open]="drawerItemId !== null"
  (closed)="closeItemSettings()"
></app-item-settings-drawer>
